import { X } from 'lucide-react';
import type { Label } from '@timato/shared';
import { cn } from '@/lib/cn';
import { colorVar } from '@/lib/projectColors';

type Props = { label: Pick<Label, 'name' | 'tint'>; onRemove?: () => void; dashed?: boolean; className?: string };

/**
 * Outline chip: no fill, no hue (8e). Tinting is opt-in and takes any project colour, custom ones included; the chip's
 * shape, not its hue, is what keeps a label from being mistaken for a project dot or a mode pill. Small (TW-44, about
 * 18px): a label annotates a title, so it never reads louder than the title beside it. The padding is uneven on
 * purpose: Manrope's line box leaves more room above the letters than below, and labels are lowercase, so even padding
 * set the text a pixel low.
 */
export function LabelChip({ label, onRemove, dashed, className }: Props) {
  const tint = label.tint ? colorVar(label.tint) : null;
  return (
    <span className={cn('inline-flex items-center gap-[3px] rounded-[4px] border px-[6px] pt-0 pb-[2px] text-[10.5px] leading-[14px] font-semibold whitespace-nowrap', dashed && 'border-dashed', tint ? '' : 'border-chip-border text-chip-fg', className)}
      style={tint ? { borderColor: tint, color: tint } : undefined}>
      {label.name}
      {onRemove && <button type="button" onClick={onRemove} aria-label={`Remove ${label.name}`} className="p-0 border-0 bg-transparent cursor-pointer inline-flex text-current opacity-70 hover:opacity-100"><X size={10} /></button>}
    </span>
  );
}

/** A row of chips truncated to "+N" (8e placement 2). */
export function LabelChips({ labels, max = 2, className }: { labels: Pick<Label, 'id' | 'name' | 'tint'>[]; max?: number; className?: string }) {
  if (!labels.length) return null;
  const shown = labels.slice(0, max), rest = labels.length - shown.length;
  return (
    <span className={cn('inline-flex items-center gap-[6px]', className)}>
      {shown.map((l) => <LabelChip key={l.id} label={l} />)}
      {rest > 0 && <span className="text-inline-meta text-ink-2" title={labels.slice(max).map((l) => l.name).join(', ')}>+{rest}</span>}
    </span>
  );
}
